import "amazon-connect-streams";
import type { Channel } from "@expert/shared-types";

export type ConnectChannel = "VOICE" | "CHAT" | "TASK";

const channelToConnectChannel: Record<Channel, ConnectChannel> = {
    voice: "VOICE",
    chat: "CHAT",
};

export interface QueueInfo {
    name: string;
    queueARN: string;
    queueId: string;
}

export interface RoutingProfileInfo {
    name: string;
    routingProfileId: string;
    routingProfileARN: string;
    channelConcurrencyMap: Partial<Record<ConnectChannel, number>>;
    queues: QueueInfo[];
    defaultOutboundQueue?: QueueInfo;
}

/**
 * Extracts routing profile info from Amazon Connect agent configuration.
 * Returns undefined if the routing profile is not available (or config is not ready yet).
 */
export function extractRoutingProfileInfo(agent: connect.Agent): RoutingProfileInfo | undefined {
    try {
        const config = agent.getConfiguration();
        const routingProfile = config.routingProfile;

        if (!routingProfile) {
            return undefined;
        }

        return {
            name: routingProfile.name,
            routingProfileId: routingProfile.routingProfileId,
            routingProfileARN: routingProfile.routingProfileARN,
            channelConcurrencyMap: routingProfile.channelConcurrencyMap ?? {},
            queues: (routingProfile.queues ?? []).map((queue) => ({
                name: queue.name,
                queueARN: queue.queueARN,
                queueId: queue.queueId,
            })),
            defaultOutboundQueue: routingProfile.defaultOutboundQueue
                ? {
                      name: routingProfile.defaultOutboundQueue.name,
                      queueARN: routingProfile.defaultOutboundQueue.queueARN,
                      queueId: routingProfile.defaultOutboundQueue.queueId,
                  }
                : undefined,
        };
    } catch {
        return undefined;
    }
}

/**
 * Checks if a routing profile supports a specific channel.
 * Accepts both lowercase Channel ("voice" | "chat") and uppercase ConnectChannel ("VOICE" | "CHAT" | "TASK").
 */
export function routingProfileSupportsChannel(
    routingProfile: RoutingProfileInfo | undefined | null,
    channel: Channel | ConnectChannel,
): boolean {
    if (!routingProfile) {
        return false;
    }

    const connectChannel =
        channel === "voice" || channel === "chat" ? channelToConnectChannel[channel] : (channel as ConnectChannel);

    const concurrency = routingProfile.channelConcurrencyMap[connectChannel];
    return concurrency !== undefined && concurrency > 0;
}
